/**
 * ChatPanel - Right side chat panel floating over the globe.
 *
 * Part of the designer's trip-detail UI layout.
 * Position: fixed, right side
 */

import { useEffect, useRef, useState } from 'react'
import { ArrowUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/style'
import { auth } from '@/api/firebase'
import {
  useSendStreamingMessage,
  useCreateThread,
  useThreadFirestore,
  useChatFirestore,
  useStreamingMessages
} from '@/hooks/chat'
import {
  getToolRenderer,
  findToolResponse,
  formatToolCopyText,
  ToolRendererErrorBoundary
} from '@/components/panels/chat/tool-renderers'
import { executeToolEffects, useToolEffectContext } from '@/domain/tool-effects'
import { Role, CompositionStatus } from '@generated/proto/model'
import type { MessageRow } from '@/domain/chat/types'

interface ChatPanelProps {
  tripId: string
}

function ChatBubble({
  message,
  isUser,
  userName,
  allMessages,
}: {
  message: MessageRow
  isUser: boolean
  userName?: string
  allMessages: MessageRow[]
}) {
  const effectContext = useToolEffectContext()

  // Determine content type at top level (before any conditional returns)
  const isToolCall = message.proto.content?.case === 'toolCall'
  const toolCall = isToolCall ? message.proto.content.value : null
  const toolName = toolCall?.toolName || ''
  const toolCallId = toolCall?.toolCallId || ''

  const toolResponse = isToolCall ? findToolResponse(allMessages, toolCallId) : null
  const isCompleted = !!toolResponse

  // Execute effects when tool completes (hook called unconditionally)
  useEffect(() => {
    if (isToolCall && isCompleted && toolResponse) {
      executeToolEffects(toolName, toolResponse, effectContext)
    }
  }, [isToolCall, isCompleted, toolName, toolResponse, effectContext])

  // Render tool calls
  if (isToolCall && toolCall) {
    const ToolRenderer = getToolRenderer(toolName)
    const copyText = formatToolCopyText(toolCall, toolResponse)

    const rendererProps = {
      toolName,
      toolCall,
      toolResponse,
      isCompleted,
      allMessages,
      copyText,
    }

    return (
      <div className="mb-4">
        <ToolRendererErrorBoundary fallbackProps={rendererProps}>
          <ToolRenderer {...rendererProps} />
        </ToolRendererErrorBoundary>
      </div>
    )
  }

  // Hide tool responses (they're displayed with their tool calls)
  if (message.proto.content?.case === 'toolResponse') {
    return null
  }

  // Handle text content
  let displayContent = ''
  let isStreaming = false

  if (message.proto.content?.case === 'textContent' && message.proto.content.value) {
    displayContent = message.proto.content.value.content || ''
    isStreaming = message.proto.content.value.compositionStatus === CompositionStatus.AGENT_STREAMING
  }

  if (!displayContent && !isStreaming) {
    return null
  }

  return (
    <div className={cn("flex mb-4", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "px-3 py-2 rounded-2xl max-w-[280px] backdrop-blur-sm",
          isUser
            ? "bg-black/70 text-white rounded-tr-sm"
            : "bg-black/50 text-white rounded-tl-sm"
        )}
      >
        <div className={cn("flex items-center gap-2 mb-2", isUser ? "flex-row-reverse" : "flex-row")}>
          {/* Avatar */}
          <div className="w-6 h-6 rounded-full overflow-hidden border border-white/30 flex-shrink-0">
            {isUser ? (
              auth.currentUser?.photoURL ? (
                <img
                  src={auth.currentUser.photoURL}
                  alt={userName || 'User'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-white/20 flex items-center justify-center text-white text-[10px] font-bold">
                  {userName?.[0]?.toUpperCase() || '?'}
                </div>
              )
            ) : (
              <video
                src="/images/hippo1.mp4"
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            )}
          </div>
          <span
            className="text-sm text-white/80 font-medium"
            style={{ fontFamily: "var(--font-alga), sans-serif" }}
          >
            {isUser ? (userName || 'You') : 'Carmen'}
          </span>
        </div>
        <p
          className="text-base whitespace-pre-wrap break-words"
          style={{ fontFamily: "var(--font-alga), sans-serif" }}
        >
          {displayContent}
          {isStreaming && <span className="inline-block w-2 h-4 bg-white/50 ml-1 animate-pulse" />}
        </p>
      </div>
    </div>
  )
}

export function ChatPanel({ tripId }: ChatPanelProps) {
  const { sendMessage, isStreaming } = useSendStreamingMessage()
  const { createThread, loading: creatingThread } = useCreateThread()
  const { threads, loading: threadsLoading } = useThreadFirestore(tripId)
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null)
  const { messages: firestoreMessages } = useChatFirestore(tripId, currentThreadId || '')
  const { messages } = useStreamingMessages(firestoreMessages)
  const [message, setMessage] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const threadCreationAttempted = useRef(false)

  const userName = auth.currentUser?.displayName || undefined

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (tripId && !currentThreadId && threads.length === 0 && !threadsLoading && !creatingThread && !threadCreationAttempted.current) {
      threadCreationAttempted.current = true
      createThread(tripId, { display: { title: 'Trip Planning Chat' } })
        .then((response: any) => {
          const threadId = response?.thread?.threadId || response?.threadId
          if (threadId) setCurrentThreadId(threadId)
        })
        .catch((error) => {
          console.error('Error creating thread:', error)
          threadCreationAttempted.current = false
        })
    } else if (threads.length > 0 && !currentThreadId) {
      const threadId = threads[0].proto.threadId
      if (threadId) setCurrentThreadId(threadId)
    }
  }, [tripId, currentThreadId, threads, threadsLoading, creatingThread, createThread])

  const handleSend = async () => {
    const content = message.trim()
    if (!content || isStreaming || creatingThread) return

    setMessage('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'

    if (!currentThreadId) {
      try {
        const response: any = await createThread(tripId, { display: { title: 'Trip Planning Chat' } })
        const threadId = response?.thread?.threadId || response?.threadId
        if (threadId) {
          setCurrentThreadId(threadId)
          await sendMessage(tripId, threadId, content, { onError: (error) => console.error('Streaming error:', error) })
        }
      } catch (error) {
        console.error('Failed to create thread or send message:', error)
      }
      return
    }

    try {
      await sendMessage(tripId, currentThreadId, content, { onError: (error) => console.error('Streaming error:', error) })
    } catch (error) {
      console.error('Failed to send message:', error)
    }
  }

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = Math.min(textarea.scrollHeight, 72) + 'px'
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const canSend = message.trim().length > 0 && !isStreaming && !creatingThread
  const recentMessages = messages.slice(-8)

  return (
    <div
      className="fixed right-4 top-[72px] bottom-[140px] w-[360px] z-40 pointer-events-auto flex flex-col"
    >
      {/* Messages Area */}
      <div
        className="flex-1 overflow-y-auto px-2 py-2"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)',
        }}
      >
        {recentMessages.map((msg) => (
          <ChatBubble
            key={msg.id}
            message={msg}
            isUser={msg.proto.role === Role.USER}
            userName={userName}
            allMessages={messages}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-black/50 backdrop-blur-md rounded-2xl shadow-2xl p-3 border border-white/20 mx-2">
        <div className="flex gap-2 items-end">
          {/* Hippo Avatar */}
          <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0">
            <video
              src="/images/hippo1.mp4"
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          </div>

          {/* Input */}
          <div className="flex-1">
            <Textarea
              ref={textareaRef}
              value={message}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your trip..."
              disabled={isStreaming || creatingThread}
              className={cn(
                "min-h-[36px] max-h-[72px] resize-none shadow-none text-base",
                "rounded-xl px-3 py-2 border-0 bg-white/10",
                "text-white placeholder:text-white/50",
                "focus-visible:ring-white/30 focus-visible:ring-1"
              )}
              style={{ fontFamily: "var(--font-alga), sans-serif" }}
              rows={1}
            />
          </div>

          {/* Send Button */}
          <Button
            onClick={handleSend}
            disabled={!canSend}
            size="icon"
            className={cn(
              "shrink-0 rounded-full h-9 w-9",
              canSend
                ? "bg-white/20 hover:bg-white/30 text-white"
                : "bg-white/5 text-white/30"
            )}
            aria-label="Send message"
          >
            <ArrowUp className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      </div>
    </div>
  )
}
